import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const PrivacyPolicyPage: React.FC = () => {
  const { t } = useLanguage();

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-2 h-2 bg-system-purple rounded-full animate-pulse shadow-neon-purple"></div>
          <span className="font-orbitron text-2xl tracking-wider text-system-purple uppercase">
            ［ Privacy Policy ］
          </span>
        </div>
        <p className="text-sm text-gray-400 font-rajdhani">
          Effective Date: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Content */}
      <div className="system-window rounded-lg p-6 max-w-4xl">
        <div className="prose prose-invert max-w-none">
          <div className="space-y-6 text-gray-300 font-rajdhani">
            {/* Important Note for International Users */}
            <div className="bg-blue-950/20 border-2 border-blue-500/50 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">🌍</span>
                <div>
                  <p className="font-orbitron font-bold text-blue-400 text-sm mb-1">
                    INTERNATIONAL USERS
                  </p>
                  <p className="text-blue-200 text-xs font-rajdhani">
                    AwakenArc is available globally. This Privacy Policy complies with GDPR (EU), CCPA (California), and other international privacy regulations. Users from different jurisdictions have specific rights detailed in Section 12.
                  </p>
                </div>
              </div>
            </div>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">1. Introduction</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc ("we," "us," "our," or "the Service") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our gamified productivity platform.
              </p>
              <p className="leading-relaxed mb-3">
                By using AwakenArc, you consent to the practices described in this Privacy Policy. If you do not agree with this policy, please do not use our Service.
              </p>
              <p className="leading-relaxed">
                We are based in Turkey and operate globally. Your data may be transferred to and processed in the United States (where our Firebase servers are located in Iowa), but we comply with international data protection laws including GDPR and CCPA.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">2. Information We Collect</h2>
              
              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">2.1 Information You Provide Directly:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Account Information:</strong> Email address, display name, password (stored as encrypted tokens, not plain text)</li>
                <li><strong>Profile Data:</strong> User preferences, settings, avatar, language selection</li>
                <li><strong>Quest Data:</strong> Tasks, goals, notes, subtasks, completion history, progress statistics</li>
                <li><strong>Program Data:</strong> Custom programs, AI-generated programs, program progress</li>
                <li><strong>Subscription Information:</strong> Subscription tier, billing cycle, payment status (payment details are stored by our payment processor, not by us)</li>
                <li><strong>AI Inputs:</strong> Prompts and information you provide to our AI quest/program generator</li>
                <li><strong>Support Communications:</strong> Messages, emails, and feedback you send us</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">2.2 Information Collected Automatically:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Device Information:</strong> Device type, operating system, browser type and version</li>
                <li><strong>Usage Data:</strong> Features used, time spent on platform, interaction patterns, quest completion rates</li>
                <li><strong>IP Address:</strong> Your IP address and general location (country/city level, not precise location)</li>
                <li><strong>Cookies and Similar Technologies:</strong> Session cookies, authentication tokens, analytics cookies (see Section 6)</li>
                <li><strong>Performance Data:</strong> Error logs, crash reports, loading times</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">2.3 Information from Third Parties:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Google OAuth:</strong> If you sign up with Google, we receive your email address and basic profile information</li>
                <li><strong>Payment Processor:</strong> Payment confirmation and subscription status (no credit card details are shared with us)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-3">
                We use your information for the following purposes:
              </p>
              
              <p className="leading-relaxed mb-2"><strong className="text-system-purple">3.1 To Provide and Improve the Service:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Create and manage your account</li>
                <li>Store and sync your quests, tasks, and progress across devices</li>
                <li>Process and track your subscriptions</li>
                <li>Generate AI-powered quests and programs using OpenAI</li>
                <li>Calculate statistics, levels, and achievements</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Improve and optimize platform performance</li>
              </ul>

              <p className="leading-relaxed mb-2"><strong className="text-system-purple">3.2 For Analytics and Research:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Understand how users interact with the platform (via Google Analytics)</li>
                <li>Analyze usage patterns to improve features</li>
                <li>Track quest completion rates and user engagement</li>
                <li>Generate anonymized statistics and insights</li>
                <li>Identify and fix bugs and technical issues</li>
              </ul>

              <p className="leading-relaxed mb-2"><strong className="text-system-purple">3.3 For Communication:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Send transactional emails (welcome, verification, password reset)</li>
                <li>Send subscription-related notifications (renewal, expiration)</li>
                <li>Send marketing emails about new features and updates (you can opt out)</li>
                <li>Send in-app notifications about achievements, levels, and system messages</li>
                <li>Respond to support requests and feedback</li>
              </ul>

              <p className="leading-relaxed mb-2"><strong className="text-system-purple">3.4 For Security and Compliance:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Detect and prevent fraud, abuse, and unauthorized access</li>
                <li>Enforce our Terms of Service</li>
                <li>Comply with legal obligations and law enforcement requests</li>
                <li>Protect our rights, property, and safety and that of our users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">4. Third-Party Services and Data Sharing</h2>
              <p className="leading-relaxed mb-3">
                We use the following third-party services to provide and improve AwakenArc. Your data may be shared with these trusted partners:
              </p>

              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">🔥 Firebase (Google Cloud Platform)</p>
                <p className="text-sm mb-2"><strong>Purpose:</strong> Authentication, database, hosting, cloud functions</p>
                <p className="text-sm mb-2"><strong>Data Shared:</strong> Email, user ID, authentication tokens, all user-generated content (quests, tasks, stats), subscription data</p>
                <p className="text-sm mb-2"><strong>Location:</strong> United States (Iowa region)</p>
                <p className="text-sm"><strong>Privacy Policy:</strong> <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">firebase.google.com/support/privacy</a></p>
              </div>

              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">📊 Google Analytics</p>
                <p className="text-sm mb-2"><strong>Purpose:</strong> Usage tracking, performance monitoring, user behavior analysis</p>
                <p className="text-sm mb-2"><strong>Data Shared:</strong> IP address (anonymized), device info, browser type, page views, feature usage, session duration</p>
                <p className="text-sm mb-2"><strong>Cookies:</strong> Yes (analytics cookies only)</p>
                <p className="text-sm"><strong>Privacy Policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">policies.google.com/privacy</a></p>
              </div>

              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">💳 Payment Processor</p>
                <p className="text-sm mb-2"><strong>Purpose:</strong> Payment processing, subscription management</p>
                <p className="text-sm mb-2"><strong>Data Shared:</strong> Email, subscription tier, billing information (handled entirely by payment processor)</p>
                <p className="text-sm mb-2"><strong>Security:</strong> PCI-DSS compliant, we never store credit card details</p>
                <p className="text-sm text-gray-500">Note: Payment processor will be updated once confirmed</p>
              </div>

              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">🤖 OpenAI</p>
                <p className="text-sm mb-2"><strong>Purpose:</strong> AI-powered quest and program generation</p>
                <p className="text-sm mb-2"><strong>Data Shared:</strong> User prompts, goal descriptions, AI generation requests (sent via API)</p>
                <p className="text-sm mb-2"><strong>Anonymization:</strong> Working to implement anonymization of prompts (coming soon)</p>
                <p className="text-sm"><strong>Privacy Policy:</strong> <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">openai.com/privacy</a></p>
              </div>

              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="font-orbitron font-bold text-white mb-2">📧 MailerSend</p>
                <p className="text-sm mb-2"><strong>Purpose:</strong> Transactional email delivery (welcome, verification, password reset, subscription notifications)</p>
                <p className="text-sm mb-2"><strong>Data Shared:</strong> Email address, display name, email content</p>
                <p className="text-sm"><strong>Privacy Policy:</strong> <a href="https://www.mailersend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">mailersend.com/legal/privacy-policy</a></p>
              </div>

              <p className="leading-relaxed mt-4">
                <strong>We do NOT:</strong> Sell your personal data to third parties, share your quest content with other users, or use your data for advertising purposes beyond analytics.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">5. Data Storage and Security</h2>
              
              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">5.1 Where Your Data is Stored:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Primary Storage:</strong> Firebase Cloud Firestore (United States - Iowa region)</li>
                <li><strong>Authentication:</strong> Firebase Authentication (encrypted tokens)</li>
                <li><strong>Backups:</strong> Automatic backups every 14 days, retained for recovery purposes</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">5.2 How We Protect Your Data:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Encryption in Transit:</strong> All data transmitted over HTTPS/SSL</li>
                <li><strong>Encryption at Rest:</strong> Data encrypted in Firebase databases</li>
                <li><strong>Password Security:</strong> Passwords stored as encrypted tokens (not plain text), never stored or transmitted unencrypted</li>
                <li><strong>Access Controls:</strong> Strict Firebase security rules limiting data access</li>
                <li><strong>Authentication:</strong> Secure Firebase Authentication with email verification required</li>
                <li><strong>Regular Updates:</strong> Security patches and updates applied promptly</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">5.3 Data Retention:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Active Accounts:</strong> Data retained indefinitely while account is active</li>
                <li><strong>Deleted Accounts:</strong> Data permanently deleted immediately upon account deletion (not kept in active database)</li>
                <li><strong>Backups:</strong> May persist in automatic backups for up to 14 days, then deleted</li>
                <li><strong>Legal Requirements:</strong> May retain certain data longer if required by law</li>
              </ul>

              <p className="leading-relaxed">
                <strong>Important:</strong> While we implement industry-standard security measures, no method of transmission or storage is 100% secure. You are responsible for maintaining the security of your account credentials.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">6. Cookies and Tracking Technologies</h2>
              
              <p className="leading-relaxed mb-3">
                We use cookies and similar technologies to provide and improve our Service. By using AwakenArc, you consent to our use of cookies as described below.
              </p>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">Types of Cookies We Use:</strong>
              </p>

              <div className="space-y-3 mb-4">
                <div className="bg-system-darker border-l-4 border-green-500 p-3 rounded">
                  <p className="font-bold text-green-400 mb-1">Essential Cookies (Required)</p>
                  <p className="text-sm">Authentication tokens, session management, security. These cannot be disabled as they are necessary for the Service to function.</p>
                </div>

                <div className="bg-system-darker border-l-4 border-blue-500 p-3 rounded">
                  <p className="font-bold text-blue-400 mb-1">Functional Cookies</p>
                  <p className="text-sm">Language preferences, theme selection, user settings. These remember your preferences to enhance your experience.</p>
                </div>

                <div className="bg-system-darker border-l-4 border-purple-500 p-3 rounded">
                  <p className="font-bold text-purple-400 mb-1">Analytics Cookies (Optional)</p>
                  <p className="text-sm">Google Analytics cookies that track usage patterns, feature engagement, and performance. You can opt out via browser settings or Google Analytics Opt-out plugin.</p>
                </div>
              </div>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">Managing Cookies:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You can control cookies through your browser settings</li>
                <li>Disabling essential cookies may prevent you from using the Service</li>
                <li>To opt out of Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">tools.google.com/dlpage/gaoptout</a></li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">7. Your Rights and Choices</h2>
              <p className="leading-relaxed mb-3">
                You have the following rights regarding your personal data:
              </p>

              <div className="space-y-3 mb-4">
                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">✅ Right to Access</p>
                  <p className="text-sm">You can view all your data through your account dashboard and settings pages.</p>
                </div>

                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">✏️ Right to Rectification</p>
                  <p className="text-sm">You can edit your profile information, quest data, and settings at any time through your account.</p>
                </div>

                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">📥 Right to Data Portability</p>
                  <p className="text-sm">You can export all your data (quests, stats, progress) in a machine-readable format through Settings → Account → Export Data.</p>
                </div>

                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">🗑️ Right to Erasure ("Right to be Forgotten")</p>
                  <p className="text-sm">You can permanently delete your account and all associated data through Settings → Account → Delete Account. Data is immediately removed from active systems and purged from backups within 14 days.</p>
                </div>

                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">📧 Right to Opt-Out of Marketing</p>
                  <p className="text-sm">You can unsubscribe from marketing emails using the unsubscribe link in any email or through your account settings.</p>
                </div>

                <div className="bg-system-darker border border-system-purple/30 p-4 rounded">
                  <p className="font-orbitron font-bold text-white mb-2">🚫 Right to Object</p>
                  <p className="text-sm">You can object to certain data processing activities by contacting us at umarkhodzha@awakenarc.com.</p>
                </div>
              </div>

              <p className="leading-relaxed">
                To exercise any of these rights or if you have questions about your data, contact us at <a href="mailto:umarkhodzha@awakenarc.com" className="text-system-purple hover:underline">umarkhodzha@awakenarc.com</a>. We will respond within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">8. Children's Privacy (COPPA Compliance)</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc is intended for users aged 13 and older. We comply with the Children's Online Privacy Protection Act (COPPA).
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Minimum age to use the Service: 13 years</li>
                <li>We require age verification (checkbox) before account creation</li>
                <li>Users under 18 should have parental permission</li>
                <li>We do not knowingly collect data from children under 13</li>
                <li>If we discover a user is under 13, we immediately delete their account and data</li>
              </ul>
              <p className="leading-relaxed">
                <strong>Parents:</strong> If you believe your child under 13 has created an account, please contact us immediately at umarkhodzha@awakenarc.com and we will delete it.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">9. International Data Transfers</h2>
              <p className="leading-relaxed mb-3">
                AwakenArc operates globally, and your data may be transferred to and processed in countries other than your own, specifically:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Primary Location:</strong> United States (Firebase Iowa region)</li>
                <li><strong>Our Location:</strong> Turkey (where the business is operated from)</li>
                <li><strong>Your Location:</strong> Wherever you are accessing the Service from</li>
              </ul>
              <p className="leading-relaxed mb-3">
                By using AwakenArc, you consent to the transfer of your data to the United States and other countries where our service providers operate. We ensure appropriate safeguards are in place:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Standard contractual clauses approved by the European Commission</li>
                <li>Compliance with EU-US Data Privacy Framework (via Google/Firebase)</li>
                <li>Security measures meeting international standards</li>
                <li>Data processing agreements with all third-party services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">10. EU Users - GDPR Rights</h2>
              <p className="leading-relaxed mb-3">
                If you are located in the European Union or European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR):
              </p>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">Legal Basis for Processing:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Contract Performance:</strong> To provide the Service you subscribed to</li>
                <li><strong>Consent:</strong> For marketing emails and optional features (you can withdraw consent anytime)</li>
                <li><strong>Legitimate Interests:</strong> For analytics, security, and service improvements</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">Your GDPR Rights:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
                <li>Right to lodge a complaint with a supervisory authority</li>
              </ul>

              <p className="leading-relaxed">
                To exercise your GDPR rights, contact us at <a href="mailto:umarkhodzha@awakenarc.com" className="text-system-purple hover:underline">umarkhodzha@awakenarc.com</a> with "GDPR Request" in the subject line. We will respond within 30 days as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">11. California Users - CCPA Rights</h2>
              <p className="leading-relaxed mb-3">
                If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):
              </p>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">Your CCPA Rights:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li><strong>Right to Know:</strong> What personal information we collect, use, disclose, and sell</li>
                <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of the sale of personal information (Note: We DO NOT sell personal data)</li>
                <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of exercising your privacy rights</li>
              </ul>

              <p className="leading-relaxed mb-3">
                <strong className="text-system-purple">What We Disclose:</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>We share data with service providers (Firebase, Google Analytics, OpenAI, MailerSend) as described in Section 4</li>
                <li>We do NOT sell your personal information</li>
                <li>We have NOT sold personal information in the past 12 months</li>
              </ul>

              <p className="leading-relaxed">
                To exercise your CCPA rights, contact us at <a href="mailto:umarkhodzha@awakenarc.com" className="text-system-purple hover:underline">umarkhodzha@awakenarc.com</a> with "CCPA Request" in the subject line. We will verify your identity and respond within 45 days.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">12. Data Breach Notification</h2>
              <p className="leading-relaxed mb-3">
                In the unlikely event of a data breach that affects your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We will notify affected users via email within 72 hours of discovering the breach</li>
                <li>We will provide details about what data was compromised</li>
                <li>We will explain steps we're taking to address the breach</li>
                <li>We will recommend actions you should take to protect yourself</li>
                <li>We will notify relevant supervisory authorities as required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">13. Changes to This Privacy Policy</h2>
              <p className="leading-relaxed mb-3">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>We will update the "Effective Date" at the top of this page</li>
                <li>We will notify you via email and in-app notification of material changes</li>
                <li>We will give you 30 days' notice before changes take effect</li>
                <li>Continued use of the Service after changes constitutes acceptance</li>
              </ul>
              <p className="leading-relaxed">
                We encourage you to review this Privacy Policy periodically. Material changes that affect your rights will be prominently announced.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-orbitron font-bold text-system-purple mb-3">14. Contact Us</h2>
              <p className="leading-relaxed mb-3">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-system-darker border border-system-purple/30 rounded-lg p-4 mb-4">
                <p className="leading-relaxed mb-2">
                  <strong className="text-system-purple">Email:</strong> <a href="mailto:umarkhodzha@awakenarc.com" className="text-system-purple hover:underline">umarkhodzha@awakenarc.com</a>
                </p>
                <p className="leading-relaxed mb-2">
                  <strong className="text-system-purple">Support Page:</strong> <a href="https://www.awakenarc.com/support" target="_blank" rel="noopener noreferrer" className="text-system-purple hover:underline">www.awakenarc.com/support</a>
                </p>
                <p className="leading-relaxed mb-2">
                  <strong className="text-system-purple">Response Time:</strong> We aim to respond to all privacy inquiries within 48 hours (or within legally required timeframes for GDPR/CCPA requests)
                </p>
                <p className="leading-relaxed">
                  <strong className="text-system-purple">Data Protection Inquiries:</strong> Use subject line "Privacy Request" or "GDPR Request" or "CCPA Request"
                </p>
              </div>

              <div className="bg-blue-950/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-400 font-rajdhani">
                  <strong>For EU Users:</strong> If you are not satisfied with our response, you have the right to lodge a complaint with your local data protection authority.
                </p>
              </div>
            </section>

            <section className="mt-8 pt-8 border-t border-gray-700">
              <p className="text-sm text-gray-500 leading-relaxed mb-3">
                This Privacy Policy is designed to be transparent about how we handle your data. We believe in respecting your privacy while providing an excellent productivity platform. Your trust is important to us.
              </p>
              <p className="text-sm text-gray-500 leading-relaxed">
                Thank you for being part of the AwakenArc community! ⚔️
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
